import type { BlockThemeConfig } from 'webapp-types';
import { BlockVisualization, ChartLegendPosition, ShownLabels } from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import VisualizationProgress from 'webapp/models-custom/visualization/progress';

const RecordLimit = 15;

export default class VisualizationRadialProgress extends VisualizationProgress {
	override visualizationType = BlockVisualization.RADIAL_PROGRESS;

	override recordLimit = RecordLimit;

	isSingleSeriesWithDefaultColor = false;

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get showValues() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.VALUE;
	}

	// ------- color schema settings --------

	override getBlockThemeConfigContent(
		configs: BlockThemeConfig.Type['visualizations'],
	): BlockThemeConfig.BlockContent {
		// take "steps" from PROGRESS (for single-series)
		return {
			order: configs.default.order,
			steps: configs[BlockVisualization.PROGRESS]?.steps,
		};
	}

	// ------- visualization - data --------

	override updateDataCache() {
		super.updateDataCache();
		this.updateColorIndexes();
	}

	updateColorIndexes() {
		this.updateColorIndexesForItems(this.series);

		// check if we have single series with default color
		// in the above case, will show gradient from the colorConfig
		let isSingleSeriesWithDefaultColor = false;
		if (this.series.length < 2) {
			let color: number | string | null = null;
			const firstSeries = this.series[0];
			if (firstSeries?.metricSelectionData) {
				// checking if a specific color is set
				color = this.blockSetup.getColorForMetricSelectionData(
					firstSeries.metricSelectionData,
				);
			}
			isSingleSeriesWithDefaultColor = color === null;
		}
		this.isSingleSeriesWithDefaultColor = isSingleSeriesWithDefaultColor;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				bc: block.colors,
				l: block.legendPosition,
				sw: this.showValues,
				o: dss.map((ds) => ds.order),
				sv: dss.map((ds) => ds.startValue),
			}),
		);
	}

	static override recordLimit = RecordLimit;
}
